import {
	Box,
	Button,
	CircularProgress,
	Divider,
	FormControl,
	FormHelperText,
	InputLabel,
	ListItem,
	ListItemText,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ContextFilterAndSort from "../ContextFilterAndSort/ContextFilterAndSort";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDateTimePicker } from "@mui/x-date-pickers/DesktopDateTimePicker";
import customHttp from "../../services/customHTTP";
import Swal from "sweetalert2";

const ManageCronJobs = () => {
	const [jobTitle, setJobTitle] = useState("");
	const [startDateTime, setStartDateTime] = useState(null);
	const [endDateTime, setEndDateTime] = useState(null);
	const [repetitionType, setRepetitionType] = useState("");
	const [apiUrl, setApiUrl] = useState("");
	const [method, setMethod] = useState("GET");
	const [authType, setAuthType] = useState("");
	const [authDetails, setAuthDetails] = useState({});
	const [reqBody, setReqBody] = useState("");
	const [response, setResponse] = useState("");
	const [errors, setErrors] = useState({});
	const [allJobs, setAllJobs] = useState([]);
	const [selectedJob, setSelectedJob] = useState(null);
	const [selectedJobTitle, setSelectedJobTitle] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [jobLoading, setJobLoading] = useState(false);

	useEffect(() => {
		const fetchJobs = async () => {
			setJobLoading(true);
			try {
				const job_resp = await customHttp.get("/scheduler/get_all_jobs");
				console.log(job_resp.data.jobs);
				setAllJobs(job_resp.data.jobs);
				setJobLoading(false);
			} catch (error) {
				console.log(error);
			}
		};
		fetchJobs();
	}, []);

	// Validation function
	const validateFields = () => {
		const newErrors = {};
		if (!jobTitle) newErrors.jobTitle = "Job Title is required.";
		if (!startDateTime) newErrors.startDateTime = "Start Date & Time is required.";
		if (!repetitionType) newErrors.repetitionType = "Repetition Type is required.";
		if (!apiUrl) newErrors.apiUrl = "API URL is required.";
		if (!method) newErrors.method = "API Method is required.";
		if (!authType) newErrors.authType = "API Authentication Type is required.";
		if (authType !== "None" && authType !== "OAuth 2.0" && !authDetails.apiKey && authType === "API Key") {
			newErrors.apiKey = "API Key is required for this authentication type.";
		}
		if (authType === "Bearer Token" && !authDetails.token) {
			newErrors.token = "Bearer Token is required.";
		}
		if (authType === "Basic Auth") {
			if (!authDetails.username) newErrors.username = "Username is required for Basic Auth.";
			if (!authDetails.password) newErrors.password = "Password is required for Basic Auth.";
		}
		return newErrors;
	};

	// Handle submit
	const handleSubmit = async (e) => {
		e.preventDefault();
		const validationErrors = validateFields();
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}
		setErrors({}); // Clear errors if all validations pass

		const jobDetails = {
			job_title: jobTitle,
			start_date_time: startDateTime,
			end_date_time: endDateTime,
			repetition_type: repetitionType,
			api_url: apiUrl,
			api_method: method,
			auth_type: authType,
			auth_details: authDetails,
			req_body: reqBody,
		};
		console.log("Job Details Submitted:", jobDetails);

		try {
			const response = await customHttp.post("/scheduler/create", { jobDetails });
			console.log(response);
			if (response.data.status === true) {
				// Trigger SweetAlert
				Swal.fire({
					title: "Success!",
					text: "Job has been created successfully.",
					icon: "success",
					confirmButtonText: "OK",
				}).then(() => {
					// Clear the form fields after successful submission
					setJobTitle("");
					setStartDateTime(null);
					setEndDateTime(null);
					setRepetitionType("");
					setApiUrl("");
					setMethod("GET");
					setAuthType("");
					setAuthDetails({});
					setReqBody("");
					setResponse(""); // Clear any response state
					setErrors({}); // Clear any error state
				});
			}
		} catch (error) {
			console.log(error);
		}
	};

	const handleSearchChange = (value) => {
		setSearchTerm(value);
	};

	const filteredJobs = allJobs.filter((job) => job.job_title.toLowerCase().includes(searchTerm.toLowerCase()));

	const handleApiTest = async () => {
		try {
			// Ensure reqBody is a valid object
			const validBody = typeof reqBody === "string" ? JSON.parse(reqBody) : reqBody;

			const headers = {
				"Content-Type": "application/json", // Always set this for JSON
			};

			// Add Authorization headers dynamically
			if (authType === "Bearer Token") {
				headers.Authorization = `Bearer ${authDetails.token}`;
			} else if (authType === "API Key") {
				headers["x-api-key"] = authDetails.apiKey;
			} else if (authType === "Basic Auth") {
				headers.Authorization = `Basic ${btoa(`${authDetails.username}:${authDetails.password}`)}`;
			}

			// Fetch API call
			const apiResponse = await fetch(apiUrl, {
				method,
				headers,
				body: method !== "GET" ? JSON.stringify(validBody) : null,
			});

			if (!apiResponse.ok) {
				// Handle non-2xx responses
				throw new Error(`HTTP error! Status: ${apiResponse.status}`);
			}

			// Parse response as JSON
			const jsonResponse = await apiResponse.json();
			setResponse(JSON.stringify(jsonResponse, null, 2)); // Pretty-print the response
		} catch (error) {
			setResponse(`Error: ${error.message}`);
			console.error("API Test Error:", error);
		}
	};

	const handleJobClick = (job) => {
		console.log(job);
		setSelectedJobTitle(job.job_title);
		setSelectedJob(job);
		setJobTitle(job.job_title);
		// setStartDateTime(job.start_date_time);
		// setEndDateTime(job.end_date_time);
		setRepetitionType(job.repetition_type);
		setApiUrl(job.api_url);
		setMethod(job.api_method);
		setAuthType(job.auth_type);
		setAuthDetails(job.auth_details);
		setReqBody(job.req_body);
		setResponse(""); // Clear any response state
		setErrors({});
	};

	return (
		<Box
			display="flex"
			flexDirection="row"
			justifyContent="flex-start"
			alignItems="flex-start"
			width="100%"
			minHeight="60vh"
			height="100%"
			padding="20px 0">
			{/* Left Side: Filter and Sort */}
			<Box
				display="flex"
				flexDirection="column"
				justifyContent="flex-start"
				alignItems="flex-start"
				width="20%"
				padding="10px 15px">
				<ContextFilterAndSort
					showSearch
					showSort={false}
					searchValue={searchTerm}
					onSearchChange={handleSearchChange}
				/>
				{allJobs.length > 0 ? (
					filteredJobs.map((job) => {
						return (
							<ListItem
								button
								key={job.id}
								onClick={() => handleJobClick(job)}
								selected={job.job_title === selectedJobTitle}
								sx={{
									padding: "12px 20px",
									borderRadius: "6px",
									"&:hover": {
										backgroundColor: "#f0f0f0",
										transition: "background-color 0.3s ease, color 0.3s ease",
									},
									backgroundColor: job.job_title === selectedJobTitle ? "#e3f2fd" : "transparent",
									color: job.job_title === selectedJobTitle ? "#1976d2" : "#000",
									transition: "background-color 0.3s ease, color 0.3s ease",
									cursor: "pointer",
								}}>
								<ListItemText
									primary={job.job_title}
									sx={{
										color: job.job_title === selectedJobTitle ? "#1976d2" : "inherit",
										fontWeight: job.job_title === selectedJobTitle ? "bold" : "normal",
									}}
								/>
							</ListItem>
						);
					})
				) : (
					<>
						{jobLoading && allJobs.length <= 0 ? (
							<Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
								<CircularProgress />
							</Box>
						) : (
							"No Jobs Found"
						)}
					</>
				)}
			</Box>

			<Divider orientation="vertical" flexItem />

			{/* Middle Section: Schedule Job Form */}
			<Box
				display="flex"
				flexDirection="column"
				justifyContent="flex-start"
				alignItems="flex-start"
				flex={1}
				padding="0 20px">
				<Typography variant="h6" fontWeight="bold">
					Schedule a New Job
				</Typography>
				<Typography variant="body1" mb={2}>
					You can schedule a new recurring job or can edit or view details from the list on the left
				</Typography>

				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<form onSubmit={handleSubmit} style={{ width: "100%" }}>
						<Box display="flex" flexDirection="column" gap={2}>
							{/* Job Title */}
							<TextField
								label="Job Title"
								size="small"
								fullWidth
								value={jobTitle}
								onChange={(e) => setJobTitle(e.target.value)}
								error={!!errors.jobTitle}
								helperText={errors.jobTitle}
							/>

							{/* Start and End Date Time */}
							<Box display="flex" gap={2} width="100%">
								<DesktopDateTimePicker
									sx={{ flex: 1 }}
									label="Start Date & Time"
									value={startDateTime}
									size="small"
									onChange={(newValue) => setStartDateTime(newValue)}
									renderInput={(params) => (
										<TextField
											{...params}
											error={!!errors.startDateTime}
											helperText={errors.startDateTime}
										/>
									)}
								/>
								<Box sx={{ flex: 1 }}>
									<DesktopDateTimePicker
										sx={{ width: "100%" }}
										label="End Date & Time"
										value={endDateTime}
										onChange={(newValue) => setEndDateTime(newValue)}
										renderInput={(params) => <TextField {...params} />}
									/>
									<Typography variant="body1" color="#aaa" fontSize="11px">
										If End Date & Time is left empty, the job will run indefinitely until canceled
									</Typography>
								</Box>
							</Box>

							{/* Repetition Type */}
							<FormControl size="small" fullWidth error={!!errors.repetitionType}>
								<InputLabel size="small" id="repeat-type">
									Repetition Type
								</InputLabel>
								<Select
									value={repetitionType}
									labelId="repeat-type"
									label="Repetition Type"
									size="small"
									onChange={(e) => setRepetitionType(e.target.value)}>
									<MenuItem value="1">Every Minute</MenuItem>
									<MenuItem value="15">Every 15 Minutes</MenuItem>
									<MenuItem value="60">Every Hour</MenuItem>
									<MenuItem value="180">Every 3 Hours</MenuItem>
									<MenuItem value="360">Every 6 Hours</MenuItem>
									<MenuItem value="720">Every 12 Hours</MenuItem>
									<MenuItem value="1440">Every Day at Start Time</MenuItem>
									<MenuItem value="43800">Every Month at Start Time</MenuItem>
								</Select>
								<FormHelperText>{errors.repetitionType}</FormHelperText>
							</FormControl>

							{/* API Details */}
							<Typography variant="h6" fontWeight="bold">
								API Details
							</Typography>
							{/* API URL and Method */}
							<Box display="flex" gap={2}>
								<FormControl size="small" sx={{ width: "12%" }}>
									<InputLabel id="method" size="small">
										Method
									</InputLabel>
									<Select
										labelId="method"
										value={method}
										label="Method"
										onChange={(e) => setMethod(e.target.value)}>
										<MenuItem value="GET">GET</MenuItem>
										<MenuItem value="POST">POST</MenuItem>
										<MenuItem value="PUT">PUT</MenuItem>
										<MenuItem value="DELETE">DELETE</MenuItem>
									</Select>
								</FormControl>
								<TextField
									label="API URL"
									size="small"
									fullWidth
									value={apiUrl}
									onChange={(e) => setApiUrl(e.target.value)}
									error={!!errors.apiUrl}
									helperText={errors.apiUrl}
								/>
							</Box>

							{/* Authentication Type */}
							<FormControl size="small" fullWidth error={!!errors.authType}>
								<InputLabel id="auth-type">Authentication Type</InputLabel>
								<Select
									labelId="auth-type"
									value={authType}
									label="Authentication Type"
									onChange={(e) => setAuthType(e.target.value)}>
									<MenuItem value="None">None</MenuItem>
									<MenuItem value="API Key">API Key</MenuItem>
									<MenuItem value="Bearer Token">Bearer Token</MenuItem>
									<MenuItem value="Basic Auth">Basic Auth</MenuItem>
									<MenuItem value="OAuth 2.0">OAuth 2.0</MenuItem>
								</Select>
								<FormHelperText>{errors.authType}</FormHelperText>
							</FormControl>

							{/* Authentication Details */}
							{authType === "API Key" && (
								<TextField
									label="API Key"
									size="small"
									fullWidth
									value={authDetails.apiKey || ""}
									onChange={(e) => setAuthDetails((prev) => ({ ...prev, apiKey: e.target.value }))}
								/>
							)}
							{authType === "Bearer Token" && (
								<TextField
									label="Token"
									size="small"
									fullWidth
									value={authDetails.token || ""}
									onChange={(e) => setAuthDetails((prev) => ({ ...prev, token: e.target.value }))}
								/>
							)}
							{authType === "Basic Auth" && (
								<Box display="flex" gap={2}>
									<TextField
										label="Username"
										size="small"
										fullWidth
										value={authDetails.username || ""}
										onChange={(e) =>
											setAuthDetails((prev) => ({
												...prev,
												username: e.target.value,
											}))
										}
									/>
									<TextField
										label="Password"
										type="password"
										size="small"
										fullWidth
										value={authDetails.password || ""}
										onChange={(e) =>
											setAuthDetails((prev) => ({
												...prev,
												password: e.target.value,
											}))
										}
									/>
								</Box>
							)}

							<TextField
								label={method === "GET" ? `Request Body (Not Required)` : `Request Body (JSON)`}
								size="small"
								fullWidth
								multiline
								minRows={3}
								value={reqBody}
								disabled={method === "GET"}
								onChange={(e) => setReqBody(e.target.value)}
							/>

							{/* Buttons */}
							<Box display="flex" gap={2} justifyContent="space-between" mt={2}>
								<Button variant="outlined" onClick={handleApiTest}>
									Test API
								</Button>
								<Button variant="contained" type="submit">
									Save Job
								</Button>
							</Box>

							{/* API Response */}
							{response && (
								<Box mt={2}>
									<Typography variant="body1" fontWeight="bold">
										Test API Response:
									</Typography>
									<pre
										style={{
											backgroundColor: "#f4f4f4",
											padding: "10px",
											borderRadius: "4px",
											overflow: "auto",
											maxHeight: "250px",
										}}>
										{response}
									</pre>
								</Box>
							)}
						</Box>
					</form>
				</LocalizationProvider>
			</Box>

			<Divider orientation="vertical" flexItem />

			{/* Right Side: Job History */}
			<Box
				display="flex"
				flexDirection="column"
				justifyContent="flex-start"
				alignItems="flex-start"
				width="25%"
				padding="0 20px">
				<Typography variant="h6" fontWeight="bold">
					Job History
				</Typography>
				<Box
					marginTop="10px"
					padding="10px"
					border="1px solid #ccc"
					borderRadius="5px"
					width="100%"
					height="200px"
					overflow="auto"
					style={{ whiteSpace: "pre-wrap" }}>
					{"No API response yet."}
				</Box>
			</Box>
		</Box>
	);
};

export default ManageCronJobs;
